import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Eye } from "lucide-react";
import { resolveImageUrl } from "../utils/images";
import { formatMoney } from "../utils/format";
import { useLanguage } from "../context/LanguageContext";
import { getProductDetailUrl } from "../utils/productSlug";
import "./ProductCard.css";

function isCatalogProduct(product) {
  return (
    product.source === "api" ||
    (typeof product.id === "string" && String(product.id).includes("-"))
  );
}

export default function ProductCard({ product, onAddToCart }) {
  const [added, setAdded] = useState(false);
  const navigate = useNavigate();
  const { language, isRTL, t } = useLanguage();
  const catalog = isCatalogProduct(product);
  const detailUrl = catalog ? getProductDetailUrl(product) : "/shop";
  const navState = catalog && product.id ? { productId: product.id } : undefined;
  const img = product.imageUrl ? resolveImageUrl(product.imageUrl) : null;

  const displayName =
    isRTL && product.arabicName && product.arabicName.trim()
      ? product.arabicName
      : product.name;

  const handleAdd = (e) => {
    e.preventDefault();
    if (catalog) {
      navigate(detailUrl, { state: navState });
      return;
    }
    setAdded(true);
    onAddToCart && onAddToCart(product);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="product-card">
      <div className="product-card__image-wrap">
        {img ? (
          <Link to={detailUrl} state={navState} className="product-card__image product-card__image--photo">
            <img src={img} alt={displayName} />
          </Link>
        ) : (
          <Link to={detailUrl} state={navState} className="product-card__image-link">
            <div
              className="product-card__image"
              style={{
                background: `linear-gradient(145deg, ${lighten(product.color, 30)} 0%, ${product.color} 100%)`,
              }}
            >
              <RobeIllustration color={product.color} borderColor={product.borderColor} />
            </div>
          </Link>
        )}

        <div className="product-card__actions">
          <Link to={detailUrl} state={navState} className="product-card__view" aria-label={t("action.viewProduct")}>
            <Eye size={15} />
          </Link>
        </div>

        <button
          type="button"
          className={`product-card__add ${added ? "added" : ""}`}
          onClick={handleAdd}
        >
          {catalog ? (
            <>
              <Eye size={14} /> {t("action.viewProduct")}
            </>
          ) : added ? (
            t("action.added")
          ) : (
            <>
              <ShoppingBag size={14} /> {t("action.addToCart")}
            </>
          )}
        </button>
      </div>

      <div className="product-card__info">
        <Link to={detailUrl} state={navState}>
          <h3 className="product-card__name">{displayName}</h3>
        </Link>
        <span className="product-card__price">{formatMoney(product.price, language)}</span>
      </div>
    </div>
  );
}

function lighten(hex, amount) {
  const safe = hex && hex.startsWith("#") ? hex : "#c9a96e";
  const num = parseInt(safe.replace("#", ""), 16);
  const r = Math.min(255, (num >> 16) + amount);
  const g = Math.min(255, ((num >> 8) & 0xff) + amount);
  const b = Math.min(255, (num & 0xff) + amount);
  return `rgb(${r},${g},${b})`;
}

function RobeIllustration({ color, borderColor }) {
  const isLight = isLightColor(color);
  const trim = borderColor || "#c9a96e";
  const fabric = color;
  const shadow = darkenColor(color, 20);

  return (
    <svg viewBox="0 0 160 220" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%", maxWidth: 130 }}>
      <path d="M45 60 L30 200 L130 200 L115 60 Z" fill={fabric} stroke={trim} strokeWidth="1.5"/>
      <path d="M80 60 L60 100 L70 200 L80 200 Z" fill={shadow} stroke={trim} strokeWidth="1"/>
      <path d="M80 60 L100 100 L90 200 L80 200 Z" fill={shadow} stroke={trim} strokeWidth="1"/>
      <path d="M45 60 L20 130 L35 140 L55 75 Z" fill={fabric} stroke={trim} strokeWidth="1.5"/>
      <path d="M115 60 L140 130 L125 140 L105 75 Z" fill={fabric} stroke={trim} strokeWidth="1.5"/>
      <path d="M65 55 L80 60 L72 90 L60 70 Z" fill={isLight ? "#f5f0e8" : "#e8ddd0"} stroke={trim} strokeWidth="1"/>
      <path d="M95 55 L80 60 L88 90 L100 70 Z" fill={isLight ? "#f5f0e8" : "#e8ddd0"} stroke={trim} strokeWidth="1"/>
      <rect x="55" y="130" width="50" height="8" rx="1" fill={trim} opacity="0.8"/>
      <rect x="75" y="128" width="10" height="12" rx="1" fill={trim}/>
      <line x1="80" y1="5" x2="80" y2="30" stroke="#b0a090" strokeWidth="2"/>
      <path d="M50 30 Q80 20 110 30" stroke="#b0a090" strokeWidth="2" fill="none"/>
      <path d="M80 5 Q90 0 88 8" stroke="#b0a090" strokeWidth="1.5" fill="none"/>
    </svg>
  );
}

function isLightColor(hex) {
  const safe = hex && hex.startsWith("#") ? hex : "#888888";
  const num = parseInt(safe.replace("#", ""), 16);
  const r = num >> 16,
    g = (num >> 8) & 0xff,
    b = num & 0xff;
  return (r * 299 + g * 587 + b * 114) / 1000 > 128;
}

function darkenColor(hex, amount) {
  const safe = hex && hex.startsWith("#") ? hex : "#888888";
  const num = parseInt(safe.replace("#", ""), 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `rgb(${r},${g},${b})`;
}
